import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit2, Trash2, X, Gift, Calendar, Sparkles } from 'lucide-react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CampaignsManagement = ({ token }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '', bonus_multiplier: '2', start_date: '', end_date: '' });

  useEffect(() => { fetchCampaigns(); }, []);

  const fetchCampaigns = async () => {
    try {
      const res = await axios.get(`${API}/admin/campaigns`, { headers: { Authorization: `Bearer ${token}` } });
      setCampaigns(res.data);
    } catch { toast.error('Kampanyalar yüklenemedi'); } finally { setLoading(false); }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.start_date || !formData.end_date) { toast.error('Gerekli alanları doldurun'); return; }
    try {
      await axios.post(`${API}/admin/campaigns`, { ...formData, bonus_multiplier: parseFloat(formData.bonus_multiplier) }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Kampanya oluşturuldu');
      setShowDialog(false); setFormData({ name: '', description: '', bonus_multiplier: '2', start_date: '', end_date: '' }); fetchCampaigns();
    } catch { toast.error('Oluşturulamadı'); }
  };

  const handleToggle = async (id) => {
    try {
      const res = await axios.put(`${API}/admin/campaigns/${id}/toggle`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message); fetchCampaigns();
    } catch { toast.error('İşlem başarısız'); }
  };

  const handleDelete = async (id) => {
    if (!confirm('Silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API}/admin/campaigns/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Silindi'); fetchCampaigns();
    } catch { toast.error('Silinemedi'); }
  };

  const isActive = (c) => { const now = new Date().toISOString(); return c.is_active && c.start_date <= now && c.end_date >= now; };

  if (loading) return <div className="text-center py-8">Yükleniyor...</div>;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-playfair text-xl font-semibold">Puan Kampanyaları</h2>
          <p className="text-sm text-charcoal/60">Özel günlerde bonus puan verin</p>
        </div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild><Button className="bg-basil text-white hover:bg-basil/90 rounded-full"><Plus className="w-4 h-4 mr-2" />Yeni Kampanya</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Yeni Puan Kampanyası</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div><Label>Kampanya Adı</Label><Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Örn: Hafta Sonu 2x Puan" className="mt-1" /></div>
              <div><Label>Açıklama</Label><Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="mt-1" /></div>
              <div><Label>Puan Çarpanı</Label>
                <Select value={formData.bonus_multiplier} onValueChange={(v) => setFormData({ ...formData, bonus_multiplier: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1.5">1.5x Puan</SelectItem>
                    <SelectItem value="2">2x Puan</SelectItem>
                    <SelectItem value="3">3x Puan</SelectItem>
                    <SelectItem value="5">5x Puan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Başlangıç</Label><Input type="datetime-local" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} className="mt-1" /></div>
                <div><Label>Bitiş</Label><Input type="datetime-local" value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} className="mt-1" /></div>
              </div>
              <Button onClick={handleSubmit} className="w-full bg-terracotta text-white hover:bg-terracotta/90">Oluştur</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-4">
        {campaigns.map((c) => (
          <div key={c.id} className={`border rounded-xl p-4 transition-colors ${isActive(c) ? 'border-basil bg-basil/5' : 'hover:border-stone-300'}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <Sparkles className={`w-5 h-5 ${isActive(c) ? 'text-basil' : 'text-charcoal/40'}`} />
                  <h3 className="font-semibold">{c.name}</h3>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${isActive(c) ? 'bg-green-100 text-green-700' : c.is_active ? 'bg-yellow-100 text-yellow-700' : 'bg-stone-100 text-stone-600'}`}>
                    {isActive(c) ? 'Aktif' : c.is_active ? 'Beklemede' : 'Pasif'}
                  </span>
                  <span className="px-2 py-1 bg-terracotta/10 text-terracotta rounded-full text-xs font-semibold">{c.bonus_multiplier}x</span>
                </div>
                {c.description && <p className="text-sm text-charcoal/60 mb-2">{c.description}</p>}
                <div className="flex items-center gap-3 text-sm text-charcoal/60">
                  <Calendar className="w-4 h-4" />
                  <span>{format(new Date(c.start_date), 'd MMM HH:mm', { locale: tr })}</span>
                  <span>→</span>
                  <span>{format(new Date(c.end_date), 'd MMM HH:mm', { locale: tr })}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={c.is_active} onCheckedChange={() => handleToggle(c.id)} />
                <button onClick={() => handleDelete(c.id)} className="p-2 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-500" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {campaigns.length === 0 && (
        <div className="text-center py-12">
          <Gift className="w-12 h-12 text-charcoal/20 mx-auto mb-4" />
          <p className="text-charcoal/60">Henüz kampanya yok</p>
        </div>
      )}
    </motion.div>
  );
};

export default CampaignsManagement;
